import { useState, useEffect } from 'react';
import './MortgageCalculator.css';

function MortgageCalculator() {
  const [loanAmount, setLoanAmount] = useState('');
  const [downPayment, setDownPayment] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('30');
  const [mortgageType, setMortgageType] = useState('fixed');
  const [monthlyPayment, setMonthlyPayment] = useState(null);

  const calculateMortgage = () => {
    const principal = parseFloat(loanAmount) - parseFloat(downPayment || 0);
    const monthlyRate = parseFloat(interestRate) / 100 / 12;
    const numberOfPayments = parseInt(loanTerm) * 12;

    if (principal > 0 && monthlyRate > 0 && numberOfPayments > 0) {
      let monthly = 0;
      
      switch (mortgageType) {
        case 'fixed':
          // Standard fixed-rate mortgage calculation
          monthly = (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                   (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
          break;
          
        case 'variable':
          // Variable rate - use current rate (in real scenario, this would fluctuate)
          // For calculation purposes, using same formula but noting it's variable
          monthly = (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                   (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
          break;
          
        case 'interest-only':
          // Interest-only payment (no principal reduction)
          monthly = principal * monthlyRate;
          break;
          
        default:
          monthly = (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                   (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
      }
      
      setMonthlyPayment(monthly.toFixed(2));
    } else {
      setMonthlyPayment(null);
    }
  };

  const handleCalculate = (e) => {
    e.preventDefault();
    calculateMortgage();
  };

  // Auto-calculate when values change
  useEffect(() => {
    if (loanAmount && interestRate && loanTerm) {
      calculateMortgage();
    }
  }, [loanAmount, downPayment, interestRate, loanTerm, mortgageType]);

  return (
    <div className="mortgage-calculator">
      <div className="container">
        <h1>Mortgage Calculator</h1>
        
        <div className="calculator-content">
          <div className="calculator-form">
            <form onSubmit={handleCalculate}>
              <div className="form-group">
                <label htmlFor="loanAmount">Home Price ($)</label>
                <input
                  type="number"
                  id="loanAmount"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  placeholder="e.g., 500000"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="downPayment">Down Payment ($)</label>
                <input
                  type="number"
                  id="downPayment"
                  value={downPayment}
                  onChange={(e) => setDownPayment(e.target.value)}
                  placeholder="e.g., 100000"
                />
              </div>

              <div className="form-group">
                <label htmlFor="interestRate">Interest Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  id="interestRate"
                  value={interestRate}
                  onChange={(e) => setInterestRate(e.target.value)}
                  placeholder="e.g., 6.5"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="mortgageType">Mortgage Type</label>
                <select
                  id="mortgageType"
                  value={mortgageType}
                  onChange={(e) => setMortgageType(e.target.value)}
                >
                  <option value="fixed">Fixed Rate Mortgage</option>
                  <option value="variable">Variable Rate Mortgage</option>
                  <option value="interest-only">Interest Only</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="loanTerm">Loan Term (years)</label>
                <select
                  id="loanTerm"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(e.target.value)}
                >
                  <option value="15">15 years</option>
                  <option value="20">20 years</option>
                  <option value="25">25 years</option>
                  <option value="30">30 years</option>
                </select>
              </div>

              <button type="submit" className="calculate-btn">
                Calculate Payment
              </button>
            </form>

            {monthlyPayment && (
              <div className="result">
                <h3>Monthly Payment</h3>
                <div className="payment-amount">${monthlyPayment}</div>
                <p className="disclaimer">
                  *This calculation is an estimate. Actual payments may vary based on taxes, insurance, and other factors.
                </p>
              </div>
            )}
          </div>

          <div className="calculator-info">
            <h3>Understanding Your Mortgage</h3>
            <div className="info-section">
              <h4>Mortgage Types</h4>
              <ul>
                <li><strong>Fixed Rate:</strong> Interest rate stays the same for the entire loan term</li>
                <li><strong>Variable Rate:</strong> Interest rate can change based on market conditions</li>
                <li><strong>Interest Only:</strong> Pay only interest for a period, no principal reduction</li>
              </ul>
            </div>


            <div className="contact-info">
              <h4>Need Help?</h4>
              <p>Contact me for personalized mortgage guidance and access to over 120 lenders.</p>
              <span className="phone-desktop">(650) 283-3537</span>
              <a href="tel:650-283-3537" className="phone-mobile contact-btn primary">Call (650) 283-3537</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MortgageCalculator;